import React from "react";
import {useForm} from "react-hook-form";
import {useMutation} from "@apollo/client";
import {LoginButton} from "../../styles/Styles";
import {ADD_COORDINATOR, GET_COORDINATORS_LIST} from "../gql/coordinators-gql";
import {toast} from "react-toastify";
import styled from "styled-components";

const FormContainer = styled.div`
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #4CAF50;
    }
`;

const ErrorMessage = styled.span`
    color: #f44336;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    display: block;
`;

const FormTitle = styled.h2`
    margin-bottom: 2rem;
    color: #333;
    text-align: center;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;

const PasswordInput = styled(Input)`
    &[type="password"] {
        letter-spacing: 0.3em;
    }
`;

type Inputs = {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
}

interface AddCoordinatorFormProps {
    onClose?: () => void;
}

const AddCoordinatorForm: React.FC<AddCoordinatorFormProps> = ({ onClose }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<Inputs>();

    const [addCoordinatorMutation] = useMutation(ADD_COORDINATOR, {
        refetchQueries: [{ query: GET_COORDINATORS_LIST }],
        onCompleted: () => {
            toast.success("Coordinator added successfully!");
            reset();
            onClose?.();
        },
        onError: (error) => {
            toast.error("Failed to add coordinator: " + error.message);
        }
    });

    const onSubmit = async (data: Inputs) => {
        try {
            await addCoordinatorMutation({
                variables: {
                    coordinatorInput: {
                        email: data.email.trim(),
                        firstName: data.firstName,
                        lastName: data.lastName,
                        password: data.password
                    }
                }
            });
        } catch (error) {
            console.error('Error adding coordinator:', error);
        }
    };

    return (
        <FormContainer>
            <FormTitle>Add New Coordinator</FormTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                        {...register("email", {
                            required: true,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address"
                            }
                        })}
                        type="email"
                        placeholder="Enter email"
                    />
                    {errors.email && <ErrorMessage>
                        {errors.email.type === "required"
                            ? "This field is required"
                            : errors.email.message}
                    </ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="password">Password</Label>
                    <PasswordInput
                        {...register("password", {
                            required: true,
                            minLength: {
                                value: 6,
                                message: "Password must be at least 6 characters"
                            }
                        })}
                        type="password"
                        placeholder="Enter password"
                    />
                    {errors.password && <ErrorMessage>
                        {errors.password.type === "required"
                            ? "This field is required"
                            : errors.password.message}
                    </ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                        {...register("firstName", { required: true })}
                        type="text"
                        placeholder="Enter first name"
                    />
                    {errors.firstName && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                        {...register("lastName", { required: true })}
                        type="text"
                        placeholder="Enter last name"
                    />
                    {errors.lastName && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <ButtonContainer>
                    <LoginButton type="submit">Add Coordinator</LoginButton>
                </ButtonContainer>
            </form>
        </FormContainer>
    );
};

export default AddCoordinatorForm;
